import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import * as Icons from "lucide-react";

/**
 * Rectangle-grid tile picker for /admin. Replaces the old pill-tab row.
 *
 * Fetches its own layout from /api/admin/admin-menu/effective so Super
 * Admin edits in Menu Directory take effect the next time an admin
 * loads the page. Tiles that the caller's role isn't allowed to see
 * are filtered server-side, so this component only renders whatever
 * came back.
 *
 * Tiles are grouped by the server-supplied `group` field for
 * quicker scanning; ordering **within** a group still respects the
 * admin's saved order. Any tile without a group falls into "Other".
 *
 * Props:
 *   - active: current tab key (e.g. "users")
 *   - onSelect: (key) => void
 */
export default function AdminMenuGrid({ active, onSelect }) {
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/admin-menu/effective");
        // Respect the saved order from Menu Directory → Admin menu so
        // admin reorder + hide edits stick. Backend already sorts by
        // `order`; we do not re-sort here.
        if (alive) setTiles(data.tiles || []);
      } catch {
        // Silent fail — Admin.jsx retains its legacy pill row as fallback
        // when tiles are empty.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Canonical group order. Any group not listed falls under "Other"
  // at the bottom so nothing gets hidden if a future tile is added
  // without updating this list.
  const GROUP_ORDER = useMemo(() => [
    "Agent",
    "Content",
    "People & Access",
    "Resources",
    "Platform",
    "Other",
  ], []);

  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const t of tiles) {
      const g = t.group && String(t.group).trim() ? t.group : "Other";
      if (!buckets.has(g)) buckets.set(g, []);
      buckets.get(g).push(t);
    }
    // Preserve canonical section order; append unknown groups after.
    const seen = new Set();
    const sections = [];
    for (const g of GROUP_ORDER) {
      if (buckets.has(g)) {
        sections.push([g, buckets.get(g)]);
        seen.add(g);
      }
    }
    for (const [g, arr] of buckets.entries()) {
      if (!seen.has(g)) sections.push([g, arr]);
    }
    return sections;
  }, [tiles, GROUP_ORDER]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i}
            className="h-24 rounded-xl bg-muted/40 animate-pulse"
            data-testid="admin-menu-grid-skeleton" />
        ))}
      </div>
    );
  }

  if (!tiles.length) return null;

  return (
    <div className="mb-8" data-testid="admin-menu-grid">
      {grouped.map(([group, groupTiles]) => (
        <section key={group} className="mb-6" data-testid={`admin-menu-group-${_slug(group)}`}>
          <h3 className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground/80 mb-2 px-1">
            {group}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {groupTiles.map((t) => (
              <Tile key={t.id} t={t} active={active} onSelect={onSelect} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}


function Tile({ t, active, onSelect }) {
  const Icon = Icons[t.icon] || Icons.Sparkles;
  const isActive = !t.path && active === t.key;
  const commonClass = [
    "group relative flex items-start gap-3 rounded-xl border p-4 text-left transition-all",
    "hover:shadow-md hover:-translate-y-0.5",
    isActive
      ? "border-primary bg-primary/5 shadow-sm ring-2 ring-primary/20"
      : "border-border bg-white",
  ].join(" ");
  const body = (
    <>
      <span
        className={[
          "shrink-0 grid place-items-center rounded-lg w-10 h-10 transition-colors",
          isActive
            ? "bg-primary text-white"
            : "bg-muted text-foreground/70 group-hover:bg-primary/10 group-hover:text-primary",
        ].join(" ")}
      >
        <Icon className="w-5 h-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-foreground truncate">
            {t.label}
          </span>
          {t.badge_count > 0 && (
            <span
              className={[
                "inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full",
                "text-[10px] font-bold uppercase tracking-wide text-white",
                t.badge_kind === "users_new_this_week" ? "bg-emerald-500" : "bg-rose-500",
              ].join(" ")}
              title={_badgeTitle(t.badge_kind, t.badge_count)}
              data-testid={`admin-tab-badge-${t.key}`}
            >
              {t.badge_count > 99 ? "99+" : t.badge_count}
            </span>
          )}
        </span>
        {t.description && (
          <span className="mt-0.5 block text-[11px] text-muted-foreground line-clamp-2">
            {t.description}
          </span>
        )}
      </span>
    </>
  );
  return t.path ? (
    <Link
      to={t.path}
      data-testid={`admin-tab-${t.key}`}
      className={commonClass}
    >
      {body}
    </Link>
  ) : (
    <button
      onClick={() => onSelect(t.key)}
      data-testid={`admin-tab-${t.key}`}
      className={commonClass}
    >
      {body}
    </button>
  );
}


function _slug(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}


function _badgeTitle(kind, n) {
  if (kind === "stories_pending")     return `${n} founder ${n === 1 ? "story" : "stories"} awaiting moderation`;
  if (kind === "students_pending")    return `${n} student ${n === 1 ? "application" : "applications"} awaiting review`;
  if (kind === "users_new_this_week") return `${n} new ${n === 1 ? "signup" : "signups"} in the last 7 days`;
  return `${n}`;
}
